import {
  AnchorButton,
  Classes,
  H3,
  PanelProps,
  Spinner,
  SpinnerSize,
} from '@blueprintjs/core';
import * as React from 'react';
import {
  ExplorerLogo,
  VoiceboxCircleLogo,
} from 'vet-bones/design-system/components';

import { TEST_IDS } from 'src/constants/testIds';
import { usePublishContext } from 'src/containers/PublishProjectDrawer/hooks';
import { ProductCard } from 'src/containers/PublishProjectDrawer/ProductCard';
import { PublishProjectDrawerStepper } from 'src/containers/PublishProjectDrawer/PublishProjectDrawerStepper';
import {
  DatabaseInfo,
  ModelAliasInfo,
  ModelsInfo,
  RulesInfo,
  VirtualGraphMappingInfoByProjectVgId,
} from 'src/containers/PublishProjectDrawer/types';
import { connectionSelector } from 'src/selectors/connection';
import { projectTargetModelIdSelector } from 'src/selectors/projectTargetModelId';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import {
  ExplorerLinkProps,
  getExplorerLink,
} from 'src/utils/web/getExplorerLink';
import { getVoiceboxLink } from 'src/utils/web/getVoiceboxLink';

export type OpenInExplorerPanelProps = {
  databaseInfo: DatabaseInfo;
  isPublishingRules: boolean;
  modelsInfo: ModelsInfo;
  modelAliasInfo?: ModelAliasInfo;
  ruleInfo: RulesInfo;
  vgMappingInfo?: VirtualGraphMappingInfoByProjectVgId;
};

export const OpenInExplorerPanel: React.VFC<
  PanelProps<OpenInExplorerPanelProps>
> = ({
  databaseInfo,
  isPublishingRules,
  modelAliasInfo,
  modelsInfo,
  ruleInfo,
  vgMappingInfo,
}) => {
  const { projectId } = useRouterParams();
  const dispatch = useReduxDispatch();
  const connection = useReduxSelector(connectionSelector);
  const targetModelId = useReduxSelector(
    projectTargetModelIdSelector(projectId)
  );
  const dashboardUrl = connection?.dashboard || '';
  const { isPending } = usePublishContext();
  const hasReasoning = isPublishingRules && Boolean(ruleInfo);

  const modelInfo = targetModelId
    ? modelsInfo[targetModelId] || Object.values(modelsInfo)[0]
    : Object.values(modelsInfo)[0];
  const linkModelName = modelAliasInfo?.modelName || modelInfo.modelName;
  const linkProps: ExplorerLinkProps = {
    connectionDashboard: dashboardUrl,
    database: databaseInfo.databaseId,
    hasVirtualGraphs: Boolean(
      vgMappingInfo && Object.keys(vgMappingInfo).length
    ),
    model: linkModelName,
    hasReasoning,
  };
  const explorerLink = getExplorerLink(linkProps);
  const voiceboxLink = getVoiceboxLink(linkProps);

  const handleClose = React.useCallback(() => {
    dispatch(uiSlice.actions.toggledPublishProjectDrawer({ isOpen: false }));
  }, [dispatch]);
  return (
    <>
      <div
        className={Classes.DRAWER_BODY}
        data-testid={TEST_IDS.publishProjectDrawer.openInExplorer.container}
      >
        <PublishProjectDrawerStepper active="none" />
        <div className="sd-drawer-publish-project__explorer-link">
          {isPending ? (
            <Spinner size={SpinnerSize.LARGE} />
          ) : (
            <>
              <H3>
                {copy.components.publishProjectDrawer.openInExplorer.success}
              </H3>
              <p>
                {
                  copy.components.publishProjectDrawer.openInExplorer
                    .successMessage
                }
              </p>

              <p>
                {
                  copy.components.publishProjectDrawer.openInExplorer
                    .explorerCard.title
                }
              </p>
              <ProductCard
                description={
                  copy.components.publishProjectDrawer.openInExplorer
                    .explorerCard.description
                }
                label={
                  copy.components.publishProjectDrawer.openInExplorer
                    .explorerCard.label
                }
                link={explorerLink}
                logo={ExplorerLogo}
                testId={TEST_IDS.publishProjectDrawer.openInExplorer.link}
              />

              {databaseInfo.shouldEnableVoicebox ? (
                <>
                  <p>
                    {
                      copy.components.publishProjectDrawer.openInExplorer
                        .voiceboxCard.title
                    }
                  </p>
                  <ProductCard
                    description={
                      copy.components.publishProjectDrawer.openInExplorer
                        .voiceboxCard.description
                    }
                    label={
                      copy.components.publishProjectDrawer.openInExplorer
                        .voiceboxCard.label
                    }
                    link={voiceboxLink}
                    logo={VoiceboxCircleLogo}
                    testId={TEST_IDS.publishProjectDrawer.openInVoicebox.link}
                  />
                </>
              ) : null}
            </>
          )}
        </div>
      </div>
      <div
        className={Classes.DRAWER_FOOTER}
        data-testid={TEST_IDS.publishProjectDrawer.footer.container}
      >
        <div className="sd-drawer-footer-actions">
          <div />
          <AnchorButton
            disabled={isPending}
            loading={isPending}
            onClick={handleClose}
            outlined
            text={copy.components.publishProjectDrawer.openInExplorer.close}
          />
        </div>
      </div>
    </>
  );
};
